import { Avatar, BasicButton, SearchInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useContactPeoples from "@/hooks/useContactPeoples";
import { cn } from "@/libraries/cn";
import { QUERY_KEYS } from "@/libraries/constant";
import { pluralize } from "@/libraries/helper";
import { getMembersQueryOptions } from "@/queries";
import {
	type CreateTeamFormSchema,
	CreateTeamIndexSearchParamSchema,
} from "@/schemas";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { onlineManager } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

export const Route = createFileRoute("/dashboard/teams/create/")({
	loaderDeps: ({ search }) => ({ directory: search.directory }),
	loader: ({ context, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getMembersQueryOptions(deps),
			);
		}
		return context.queryClient.getQueryData([QUERY_KEYS.GET_MEMBERS, deps]);
	},
	validateSearch: zodSearchValidator(CreateTeamIndexSearchParamSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (s) => s.q ?? "",
	});
	const isDirectory = Route.useSearch({
		select: (s) => s.directory,
	});

	const {
		scrollRef,
		peoples,
		data: allPeoples,
		virtualizer,
	} = useContactPeoples({
		isDirectory,
		excludeProfile: true,
		search: searchQ,
		estimateSize: 65,
		gap: 0,
	});

	const { watch, setValue } = useFormContext<CreateTeamFormSchema>();
	const selectedUserIds = watch("user_ids", new Set([]));
	const arrSelectedUserIds = Array.from(selectedUserIds);

	const onClickSelectAll = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			const allPeopleIds = new Set(allPeoples.map((a) => a.id) ?? []);
			return setValue(
				"user_ids",
				selectedUserIds.size === allPeopleIds.size ? new Set([]) : allPeopleIds,
			);
		},
		[setValue, allPeoples, selectedUserIds],
	);

	const onClickNext = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			return navigate({
				to: "/dashboard/teams/create/name",
				search: {
					directory: isDirectory,
				},
			});
		},
		[navigate, isDirectory],
	);

	return (
		<div className="h-full flex flex-col overflow-hidden">
			<BackAppBar title="Add Team" subTitle="Add Members">
				<div className="container pb-2 flex flex-col gap-2">
					<div className="flex flex-row justify-between items-center">
						<Typography fontWeight={700}>Contacts</Typography>
						{virtualizer.items.length ? (
							<Button onClick={onClickSelectAll}>Select All</Button>
						) : null}
					</div>
					{allPeoples.length ? (
						<SearchInput
							name="search"
							label="Search"
							placeholder="Search"
							value={searchQ}
							onChange={(e) =>
								navigate({
									replace: true,
									search: {
										q: e.target.value || undefined,
										directory: isDirectory,
									},
								})
							}
						/>
					) : null}
				</div>
			</BackAppBar>
			<div
				ref={scrollRef}
				className="relative container flex-1 flex flex-col overflow-y-auto"
			>
				<List
					disablePadding
					className="relative divide-y divide-gray-light"
					style={{
						height: virtualizer.getTotalSize(),
					}}
				>
					{virtualizer.items.length ? (
						<>
							{virtualizer.items.map((rowItem) => {
								const people = peoples[rowItem.index];
								if (!people) return;
								return (
									<ListItem
										ref={virtualizer.measureElement}
										key={rowItem.key}
										data-index={rowItem.index}
										disableGutters
										className={cn("!absolute !top-0 !left-0 w-full", {
											"bg-primary-light": selectedUserIds.has(people.id),
										})}
										style={{
											transform: `translateY(${rowItem.start}px)`,
										}}
									>
										<ListItemButton dense>
											<ListItemIcon>
												<Checkbox
													value={people.id}
													checked={selectedUserIds.has(people.id)}
													edge="start"
													tabIndex={-1}
													disableRipple
													className="peer:"
													onChange={(e) =>
														setValue(
															"user_ids",
															new Set(
																e.target.checked
																	? [...arrSelectedUserIds, people.id]
																	: arrSelectedUserIds.filter(
																			(id) => id !== people.id,
																		),
															),
														)
													}
												/>
											</ListItemIcon>
											<ListItemAvatar>
												<Avatar
													alt={people.first_name || "L"}
													className="!bg-gray-light !text-dark"
													src={people.avatar ?? "/broken-image.jpg"}
												/>
											</ListItemAvatar>
											<ListItemText primary={people.fullname} />
										</ListItemButton>
									</ListItem>
								);
							})}
						</>
					) : (
						<ListItem disableGutters>
							<ListItemText
								primary={
									<Typography
										fontWeight={700}
										align="center"
										className="!text-gray-200"
									>
										{searchQ
											? `No results found for ‘${searchQ}’`
											: "No contacts"}
									</Typography>
								}
							/>
						</ListItem>
					)}
				</List>
			</div>
			{selectedUserIds.size ? (
				<div className="container py-4 shadow grid grid-cols-2 justify-between items-center">
					<Typography fontWeight={500}>
						{pluralize({
							count: selectedUserIds.size,
							singularText: "Contact",
						})}{" "}
						Selected
					</Typography>
					<BasicButton
						variant="contained"
						color="secondary"
						className="w-full !h-12 text-sm font-semibold !shadow-none"
						disable={!selectedUserIds.size}
						onClick={onClickNext}
					>
						Next
					</BasicButton>
				</div>
			) : null}
		</div>
	);
}
